import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, Plus, Trash2, Calculator, Sparkles } from 'lucide-react';
import '../../styles/design-system.css';

const GoalsPlanner = ({ onClose }) => {
  const [goals, setGoals] = useState([]);
  const [newGoal, setNewGoal] = useState({
    name: '',
    targetValue: '',
    deadline: '',
    currentSaved: ''
  });
  const [showResults, setShowResults] = useState(false);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const parseCurrency = (value) => {
    return parseFloat(value.replace(/\D/g, '')) / 100 || 0;
  };

  const handleCurrencyChange = (field, value) => {
    let numValue = value.replace(/\D/g, '');
    if (numValue) {
      numValue = (parseInt(numValue) / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      });
    }
    setNewGoal({ ...newGoal, [field]: numValue });
  };

  const addGoal = () => {
    if (!newGoal.name || !newGoal.targetValue || !newGoal.deadline) return;

    const target = parseCurrency(newGoal.targetValue);
    const current = parseCurrency(newGoal.currentSaved) || 0;
    const deadlineDate = new Date(newGoal.deadline);
    const today = new Date();
    const monthsRemaining = Math.max(1, 
      (deadlineDate.getFullYear() - today.getFullYear()) * 12 + 
      (deadlineDate.getMonth() - today.getMonth())
    );
    
    const remaining = target - current;
    const monthlyNeeded = remaining / monthsRemaining;

    const goal = {
      id: Date.now(),
      name: newGoal.name,
      targetValue: target,
      currentSaved: current,
      deadline: newGoal.deadline,
      monthsRemaining,
      monthlyNeeded,
      remaining,
      progress: (current / target) * 100
    };

    setGoals([...goals, goal]);
    setNewGoal({ name: '', targetValue: '', deadline: '', currentSaved: '' });
  };

  const removeGoal = (id) => {
    setGoals(goals.filter(g => g.id !== id));
  };

  const calculateTotal = () => {
    return goals.reduce((sum, g) => sum + g.monthlyNeeded, 0);
  };

  const goalIcons = ['🏠', '🚗', '✈️', '📚', '💍', '👶', '🎓', '💼', '🏖️', '💰'];

  return (
    <div className="p-6 max-h-[80vh] overflow-y-auto">
      <div className="flex items-center gap-3 mb-6">
        <div 
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{ background: 'var(--accent-pink-200)' }}
        >
          <Target size={24} style={{ color: 'var(--text-primary)' }} />
        </div>
        <div>
          <h3 className="heading-2">Planejador de Metas Financeiras</h3>
          <p className="caption">Defina suas metas e saiba quanto guardar</p>
        </div>
      </div>

      {!showResults ? (
        <>
          {/* Add Goal Form */}
          <div className="voice-card accent-grey mb-6">
            <h4 className="body-medium font-medium mb-4">Adicionar Nova Meta</h4>
            
            <div className="space-y-4">
              <div>
                <label className="caption block mb-1">Nome da meta</label>
                <input
                  type="text"
                  value={newGoal.name}
                  onChange={(e) => setNewGoal({ ...newGoal, name: e.target.value })}
                  placeholder="Ex: Viagem dos sonhos"
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ 
                    background: 'var(--bg-card)', 
                    border: '1px solid var(--border-input)'
                  }}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="caption block mb-1">Valor da meta</label>
                  <input
                    type="text"
                    value={newGoal.targetValue}
                    onChange={(e) => handleCurrencyChange('targetValue', e.target.value)}
                    placeholder="R$ 0,00"
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{ 
                      background: 'var(--bg-card)', 
                      border: '1px solid var(--border-input)'
                    }}
                  />
                </div>
                <div>
                  <label className="caption block mb-1">Já guardou?</label>
                  <input
                    type="text"
                    value={newGoal.currentSaved}
                    onChange={(e) => handleCurrencyChange('currentSaved', e.target.value)}
                    placeholder="R$ 0,00"
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{ 
                      background: 'var(--bg-card)', 
                      border: '1px solid var(--border-input)'
                    }}
                  />
                </div>
              </div>

              <div>
                <label className="caption block mb-1">Prazo (quando quer alcançar?)</label>
                <input
                  type="date"
                  value={newGoal.deadline}
                  onChange={(e) => setNewGoal({ ...newGoal, deadline: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ 
                    background: 'var(--bg-card)', 
                    border: '1px solid var(--border-input)'
                  }}
                />
              </div>

              <button
                onClick={addGoal}
                disabled={!newGoal.name || !newGoal.targetValue || !newGoal.deadline}
                className="btn-secondary w-full flex items-center justify-center gap-2"
                style={{ opacity: (newGoal.name && newGoal.targetValue && newGoal.deadline) ? 1 : 0.5 }}
              >
                <Plus size={18} />
                Adicionar Meta
              </button>
            </div>
          </div>

          {/* Goals List */}
          {goals.length > 0 && (
            <div className="mb-6">
              <h4 className="body-medium font-medium mb-3">Suas Metas ({goals.length})</h4>
              <div className="space-y-3">
                <AnimatePresence>
                  {goals.map((goal, index) => (
                    <motion.div
                      key={goal.id}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      className="p-4 rounded-xl"
                      style={{ background: 'var(--bg-section)' }}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{goalIcons[index % goalIcons.length]}</span>
                          <span className="body-medium font-medium">{goal.name}</span>
                        </div>
                        <button
                          onClick={() => removeGoal(goal.id)}
                          className="text-red-400 hover:text-red-600"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      {/* Progress Bar */}
                      <div className="mb-2">
                        <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--border-light)' }}>
                          <div 
                            className="h-full rounded-full transition-all"
                            style={{ 
                              width: `${Math.min(100, goal.progress)}%`,
                              background: goal.progress >= 100 ? '#4CAF50' : 'var(--accent-purple-400)'
                            }}
                          />
                        </div>
                        <div className="flex justify-between mt-1">
                          <span className="caption">{formatCurrency(goal.currentSaved)}</span>
                          <span className="caption">{formatCurrency(goal.targetValue)}</span>
                        </div>
                      </div>

                      <div className="flex justify-between text-sm">
                        <span className="caption">
                          {goal.monthsRemaining} meses restantes
                        </span>
                        <span className="body-small font-medium" style={{ color: 'var(--accent-purple-400)' }}>
                          {formatCurrency(goal.monthlyNeeded)}/mês
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          )}

          {/* Calculate Button */}
          {goals.length > 0 && (
            <button
              onClick={() => setShowResults(true)}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              <Calculator size={18} />
              Ver Plano Completo
            </button>
          )}

          {goals.length === 0 && (
            <div className="text-center py-8">
              <Target size={48} className="mx-auto mb-4 opacity-30" />
              <p className="body-small" style={{ color: 'var(--text-muted)' }}>
                Adicione suas metas financeiras para começar o planejamento
              </p>
            </div>
          )}
        </>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {/* Summary Card */}
          <div 
            className="text-center p-6 rounded-xl mb-6"
            style={{ background: 'var(--gradient-hero-subtle)' }}
          >
            <Sparkles size={32} className="mx-auto mb-3" style={{ color: 'var(--accent-purple-400)' }} />
            <p className="caption mb-2">Para alcançar todas as suas metas, você precisa guardar</p>
            <p className="heading-hero">{formatCurrency(calculateTotal())}</p>
            <p className="body-small">por mês</p>
          </div>

          {/* Goals Breakdown */}
          <div className="mb-6">
            <h4 className="body-medium font-medium mb-3">Detalhamento por Meta</h4>
            <div className="space-y-3">
              {goals.map((goal, index) => (
                <div 
                  key={goal.id}
                  className="p-4 rounded-xl flex items-center justify-between"
                  style={{ background: 'var(--bg-section)' }}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{goalIcons[index % goalIcons.length]}</span>
                    <div>
                      <p className="body-medium font-medium">{goal.name}</p>
                      <p className="caption">
                        Meta: {formatCurrency(goal.targetValue)} em {goal.monthsRemaining} meses
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="heading-3" style={{ color: 'var(--accent-purple-400)' }}>
                      {formatCurrency(goal.monthlyNeeded)}
                    </p>
                    <p className="caption">/mês</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tips */}
          <div className="voice-card accent-green mb-4">
            <p className="body-small font-medium mb-2">💡 Dicas para alcançar suas metas:</p>
            <ul className="space-y-1">
              <li className="caption">• Automatize as transferências no dia do pagamento</li>
              <li className="caption">• Separe uma conta específica para cada meta</li>
              <li className="caption">• Revise mensalmente seu progresso</li>
              <li className="caption">• Celebre pequenas conquistas no caminho</li>
            </ul>
          </div>

          {/* Biblical Quote */}
          <div className="voice-card accent-purple mb-4">
            <p className="caption italic">
              "Escreva a visão, grave-a em tábuas, para que possa ser lida facilmente." - Habacuque 2:2
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setShowResults(false)}
              className="btn-secondary flex-1"
            >
              Editar Metas
            </button>
            <button onClick={onClose} className="btn-primary flex-1">
              Quero Ajuda Profissional
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default GoalsPlanner;
